export const icons = [
  'ghost',
  'witch',
  'monster',
  'skull',
  'bat',
  'pumpkin',
  'vampire',
  'spider',
  'cat',
  'owl',
  'moon',
  'crystal',
  'cobweb',
  'candy',
  'lollipop',
  'bone',
  'coffin',
  'tombstone',
  'haunted',
  'broom',
  'wolf',
  'crow',
  'potion',
  'candle',
  'mummy',
  'cauldron',
] as const;
/** Player avatars use the same icon set as teams (no team colors). */
export const avatars = icons;
export const taskTypes = ['qr', 'photo', 'manual', 'trivia'] as const;
export const DEFAULT_SUCCESS_MESSAGE = 'Challenge completed!';

/**
 * Powerups and first-finisher boosts are switched off until the feature is ready. While off,
 * the UI hides them and the server neither grants them nor applies 2x points.
 */
export const POWERUPS_ENABLED = false;

export const powerupKinds = ['steal', 'peek', 'hint', 'dare'] as const;
export type PowerupKind = (typeof powerupKinds)[number];

export const STEAL_POINTS = 100;
export const PEEK_SECONDS = 60;
export const BOOST_SECONDS = 180;
export const BOOST_MULTIPLIER = 2;
export const DEFAULT_DARE = 'Take a shot!';

export const powerupMeta: Record<
  PowerupKind,
  { title: string; description: string; needsTarget: boolean }
> = {
  steal: {
    title: 'Steal',
    description: `Take up to ${STEAL_POINTS} points from another team.`,
    needsTarget: true,
  },
  peek: {
    title: 'Peek',
    description: `See every team's score for ${PEEK_SECONDS} seconds.`,
    needsTarget: false,
  },
  hint: {
    title: 'Hint',
    description: 'Get a clue to a QR code your team has not found yet.',
    needsTarget: false,
  },
  dare: {
    title: 'Dare',
    description: 'Send another team a dare they have to do.',
    needsTarget: true,
  },
};
export const triviaAnswerModes = ['free_response', 'multiple_choice'] as const;
export const taskStatuses = ['draft', 'published', 'closed'] as const;

export const taskStatusMeta: Record<
  (typeof taskStatuses)[number],
  { title: string; description: string }
> = {
  draft: {
    title: 'Draft',
    description: 'Not visible to players',
  },
  published: {
    title: 'Published',
    description: 'Open for completions',
  },
  closed: {
    title: 'Closed',
    description: 'Can stay visible, but no new submissions',
  },
};

export const taskTypeMeta: Record<
  (typeof taskTypes)[number],
  { title: string; description: string }
> = {
  qr: {
    title: 'QR Discovery',
    description: 'Players scan a printed QR code you hide around the party.',
  },
  photo: {
    title: 'Photo Challenge',
    description: 'Players submit a photo for host approval and the party display.',
  },
  manual: {
    title: 'Manual Challenge',
    description: 'A physical or in-person activity the host verifies on the spot.',
  },
  trivia: {
    title: 'Trivia / Answer',
    description: 'Players answer a question, riddle, or short prompt.',
  },
};

export const gameStatuses = [
  'setup',
  'registration',
  'live',
  'paused',
  'ended',
  'awards',
  'archived',
] as const;
export const teamColors = [
  { name: 'Violet', value: '#7c3aed' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Emerald', value: '#22c55e' },
  { name: 'Crimson', value: '#ef4444' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orchid', value: '#a855f7' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Rose', value: '#e11d48' },
  { name: 'Iris', value: '#8b5cf6' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Fuchsia', value: '#d946ef' },
  { name: 'Burnt Orange', value: '#ea580c' },
  { name: 'Slate', value: '#64748b' },
  { name: 'Gold', value: '#facc15' },
  { name: 'Violet Bloom', value: 'linear-gradient(135deg, #7c3aed, #ec4899)' },
  { name: 'Ember', value: 'linear-gradient(135deg, #f59e0b, #ef4444)' },
  { name: 'Ice Hex', value: 'linear-gradient(135deg, #06b6d4, #7c3aed)' },
  { name: 'Toxic Tide', value: 'linear-gradient(135deg, #22c55e, #06b6d4)' },
  { name: 'Night Violet', value: 'linear-gradient(135deg, #111827, #7c3aed)' },
  { name: 'Blood Magic', value: 'linear-gradient(135deg, #ef4444, #7c3aed)' },
  { name: "Jack-O'-Lantern", value: 'linear-gradient(135deg, #f97316, #fbbf24)' },
  { name: 'Deep Haunt', value: 'linear-gradient(135deg, #1e1b4b, #4c1d95)' },
  { name: 'Slime', value: 'linear-gradient(135deg, #065f46, #22c55e)' },
  { name: 'Candy Corn', value: 'linear-gradient(135deg, #9f1239, #fb7185)' },
  { name: 'Moonlit', value: 'linear-gradient(135deg, #0ea5e9, #e0f2fe)' },
  { name: 'Molten', value: 'radial-gradient(circle at 30% 30%, #fde68a, #b45309 55%, #431407)' },
  { name: 'Witchfire', value: 'linear-gradient(135deg, #312e81, #c026d3)' },
  { name: 'Potion Glow', value: 'linear-gradient(135deg, #0f766e, #fbbf24)' },
  { name: 'Specter Stripe', value: 'linear-gradient(90deg, #ef4444, #f59e0b, #22c55e)' },
  { name: 'Grave Smoke', value: 'linear-gradient(135deg, #18181b, #52525b)' },
  {
    name: 'Vampire Kiss',
    value: 'radial-gradient(circle at 70% 20%, #fda4af, #9f1239 60%, #450a0a)',
  },
  { name: 'Midnight Frost', value: 'linear-gradient(160deg, #67e8f9, #2563eb 45%, #1e1b4b)' },
  { name: 'Bog Light', value: 'linear-gradient(135deg, #fef08a, #84cc16 40%, #14532d)' },
  {
    name: 'Phantom Mist',
    value: 'radial-gradient(circle at 40% 40%, #e9d5ff, #6b21a8 50%, #1e1b4b)',
  },
];

export function teamColorName(color: string): string {
  return teamColors.find((entry) => entry.value === color)?.name ?? 'Custom';
}

export function teamAccentColor(color: string): string {
  const match = color.match(/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/);
  if (!match) return '#7c3aed';
  const hex = match[0];
  if (hex.length === 4) {
    const [, r, g, b] = hex;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return hex;
}

export const halloweenTeamAdjectives = [
  'Spooky',
  'Haunted',
  'Creepy',
  'Ghoulish',
  'Sinister',
  'Cursed',
  'Shadowy',
  'Wicked',
  'Eerie',
  'Macabre',
  'Phantom',
  'Howling',
  'Midnight',
  'Bone-chilling',
  'Cryptic',
  'Spectral',
  'Grim',
  'Moonlit',
  'Bewitched',
  'Chilling',
  'Frightful',
  'Ghastly',
  'Morbid',
  'Twisted',
  'Vampire',
];

export const halloweenTeamNouns = [
  'Pumpkins',
  'Ghosts',
  'Bats',
  'Witches',
  'Goblins',
  'Skeletons',
  'Zombies',
  'Ravens',
  'Spiders',
  'Monsters',
  'Ghouls',
  'Vampires',
  'Werewolves',
  'Reapers',
  'Phantoms',
  'Scarecrows',
  'Coffins',
  'Cauldrons',
  'Cobwebs',
  'Crypts',
  'Nightmares',
  'Specters',
  "Jack-o'-Lanterns",
  'Banshees',
  'Mummies',
];

function pickRandom<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

export function randomHalloweenTeamName(): string {
  return `${pickRandom(halloweenTeamAdjectives)} ${pickRandom(halloweenTeamNouns)}`;
}

export const displayModes = ['leaderboard', 'ended', 'award'] as const;
export const awardSteps = ['third', 'second', 'individual', 'first'] as const;
export const awardStepMeta: Record<(typeof awardSteps)[number], { title: string }> = {
  third: { title: 'Third Place' },
  second: { title: 'Second Place' },
  individual: { title: 'MVP' },
  first: { title: 'First Place' },
};

export type Avatar = (typeof avatars)[number];
export type TeamIcon = (typeof icons)[number];
export type TaskType = (typeof taskTypes)[number];
export type TriviaAnswerMode = (typeof triviaAnswerModes)[number];
export type TaskStatus = (typeof taskStatuses)[number];
export type GameStatus = (typeof gameStatuses)[number];
export type DisplayMode = (typeof displayModes)[number];
export type AwardStep = (typeof awardSteps)[number];
